import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  QuoteItemDigiQuoteData,
  DigiQuoteAddOn,
  DigiQuoteQuantity,
  DigiQuoteExpedite,
  DigiQuoteDiscount,
  DigiQuoteAddOnCell,
} from '@frontend/api';
import {
  PartThumbnail,
  VStack,
  HStack,
  Text,
  Part,
  PartLabel,
  PartThumbnailContainer,
  Divider,
  TableContainer,
  TableV2,
  Thead,
  Th,
  Tr,
  Tbody,
  TableFooter,
  ButtonGroup,
  Button,
  Td,
  Checkbox,
  Radio,
  Box,
  Circle,
  CheckmarkIcon,
  CloseIcon,
  IconButton,
  Tooltip,
  OutlineCircleInfoIcon,
  DatePicker,
} from '@frontend/components';
import { toMoment } from '@frontend/utils';

import { LeadTimeUnit } from 'constants/all';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useAppSelector } from 'store/hooks';
import { getOrder } from 'store/orders/selectors';
import { useFeatureFlagValue } from 'store/session/hooks';
import { ActionPayload } from 'store/types';

import { formatPercent, formatPrice } from 'utils/formatters';
import { formatLeadTimeLabel } from 'utils/formatters';
import { uuid } from 'utils/stringUtils';

import { DRAWER_MODES } from '../constants';
import * as actions from '../data/actions';
import {
  getAdjustedLeadTime,
  getAdjustedShipsOn,
  getAdjustment,
  getDrawerMode,
  getSearchQuery,
  getSelectedOption,
} from '../data/reducer';
import { SelectedOption } from '../data/types';
import {
  getLeadTimeFromShipsOnDate,
  getShipsOnDateFromLeadTime,
} from '../utils';
import { AdditionalCharges } from './AdditionalCharges';
import { Adjustment } from './Adjustment';

type AddOnsTableProps = {
  quoteItem: QuoteItemDigiQuoteData;
  quantity: DigiQuoteQuantity;
};

const AddOnsTable = ({ quoteItem, quantity }: AddOnsTableProps) => {
  const dispatch = useDispatch();
  const selectedOption = useAppSelector((state) =>
    getSelectedOption(state, quoteItem.id),
  );

  const selectAddOn = (addOn: DigiQuoteAddOnCell) => {
    dispatch(actions.selectAddOn({ addOn, quoteItemId: quoteItem.id }));
  };

  const addOnsMap = useMemo(
    () =>
      quoteItem.rootComponent.addOns?.reduce(
        (map, addOn) => ({ [addOn.id]: addOn, ...map }),
        {} as Record<number, DigiQuoteAddOn>,
      ),
    [quoteItem.rootComponent],
  );

  return (
    <Box>
      <HStack justifyContent="space-between">
        <Text size="sm" fontWeight="heavy">
          Additional Charges
        </Text>
        <Text size="sm" fontWeight="heavy">
          Price
        </Text>
      </HStack>
      <VStack
        divider={<Divider borderColor="separator.border" />}
        spacing={0}
        alignItems="stretch"
      >
        {quoteItem.rootComponent.addOnCells
          .filter((cell) => cell.quantityId === quantity.id)
          .map((addOnCell) => (
            <HStack
              justifyContent="space-between"
              key={addOnCell.addOnId}
              py={0.25}
            >
              <Checkbox
                size="sm"
                isReadOnly={addOnsMap[addOnCell.addOnId].isRequired}
                isChecked={!!selectedOption.addOns[addOnCell.addOnId]}
                onChange={() => selectAddOn(addOnCell)}
              >
                {addOnsMap[addOnCell.addOnId].name}
                {addOnsMap[addOnCell.addOnId].isRequired
                  ? ' (Required)'
                  : ' (Optional)'}
              </Checkbox>
              <Text size="sm" fontFamily="data">
                {formatPrice(
                  addOnCell.manualPrice
                    ? addOnCell.manualPrice
                    : addOnCell.price,
                  2,
                )}
              </Text>
            </HStack>
          ))}
      </VStack>
    </Box>
  );
};

type DiscountsTableProps = {
  quoteItem: QuoteItemDigiQuoteData;
  quantity: DigiQuoteQuantity;
};

const DiscountsTable = ({ quoteItem, quantity }: DiscountsTableProps) => {
  const discountsMap = useMemo(
    () =>
      quoteItem.rootComponent.discounts?.reduce(
        (map, discount) => ({ [discount.id]: discount, ...map }),
        {} as Record<number, DigiQuoteDiscount>,
      ),
    [quoteItem.rootComponent],
  );

  return (
    <Box>
      <HStack justifyContent="space-between">
        <Text size="sm" fontWeight="heavy">
          Discounts
        </Text>
        <Text size="sm" fontWeight="heavy">
          Percent
        </Text>
      </HStack>
      <VStack
        divider={<Divider borderColor="separator.border" />}
        spacing={0}
        alignItems="stretch"
      >
        {quoteItem.rootComponent.discountCells
          .filter(
            (cell) =>
              cell.quantityId === quantity.id &&
              (cell.manualPercentage ?? cell.percentage) !== 0,
          )
          .map((discountCell) => (
            <HStack
              justifyContent="space-between"
              key={`discount-row-${quantity.id}-${discountCell.discountId}`}
              py={0.25}
            >
              <Checkbox size="sm" isReadOnly isChecked>
                {discountsMap[discountCell.discountId]!.name}
              </Checkbox>
              <Text size="sm" fontFamily="data">
                {formatPercent(
                  discountCell.manualPercentage ?? discountCell.percentage,
                )}
              </Text>
            </HStack>
          ))}
      </VStack>
    </Box>
  );
};

type OptionDetailsProps = {
  quoteItem: QuoteItemDigiQuoteData;
  quantity: DigiQuoteQuantity;
  expedite?: DigiQuoteExpedite;
  isSelected: boolean;
  itemSelected: boolean;
  onDeselect: () => void;
  onSelect: () => void;
  darkBg: boolean;
};

const OptionDetails = ({
  quoteItem,
  quantity,
  expedite,
  isSelected,
  itemSelected,
  onDeselect,
  onSelect,
}: OptionDetailsProps) => {
  const dispatch = useDispatch();
  const drawerMode = useAppSelector(getDrawerMode);
  const order = useAppSelector(getOrder);
  const orderCreatedDate =
    drawerMode === DRAWER_MODES.EDIT && order ? order.created : undefined;

  const option = expedite ?? quantity;
  const unitPrice =
    expedite?.unitPrice ??
    quantity.manualTotalUnitPrice ??
    quantity.totalUnitPrice;

  const hasDiscounts =
    quoteItem.rootComponent.discountCells?.some(
      (cell) =>
        cell.quantityId === quantity.id &&
        (cell.manualPercentage ?? cell.percentage) !== 0,
    ) ?? false;

  const hasAddOns =
    quoteItem.rootComponent.addOnCells?.some(
      (cell) => cell.quantityId === quantity.id,
    ) ?? false;

  const cellTextStyles =
    itemSelected && !isSelected
      ? {
          color: 'feature.secondary',
        }
      : null;

  const adjustedLeadTime = useAppSelector((state) =>
    getAdjustedLeadTime(state, expedite?.id || quantity.id),
  );
  const adjustedShipsOn = useAppSelector((state) =>
    getAdjustedShipsOn(state, expedite?.id || quantity.id),
  );

  const leadTime = adjustedLeadTime ?? option.manualLeadTime ?? option.leadTime;

  return (
    <>
      <Tr isSelected={isSelected}>
        <Td textAlign="right" cursor="pointer">
          <Radio
            isChecked={isSelected}
            onClick={() => {
              if (isSelected) {
                onDeselect();
              } else {
                onSelect();
              }
            }}
          />
        </Td>
        <Td textAlign="right" {...cellTextStyles}>
          {quantity.quantity}
        </Td>
        <Td textAlign="right" {...cellTextStyles}>
          {formatLeadTimeLabel(leadTime, {
            unit: option.leadTimeDisplayUnit,
          }).toLowerCase()}
        </Td>
        <Td
          paddingX={0.75}
          sx={{
            '.ant-picker.ant-picker-small': {
              paddingX: 0.75,
              height: '100%',
            },
            '.ant-picker-input > input': {
              fontSize: 'sm',
            },
          }}
        >
          <DatePicker
            size="small"
            allowClear={false}
            disabledDate={(current) => current.isBefore(moment(), 'day')}
            value={
              adjustedShipsOn
                ? toMoment(adjustedShipsOn)
                : toMoment(
                    getShipsOnDateFromLeadTime({
                      fromDate: orderCreatedDate
                        ? new Date(orderCreatedDate)
                        : new Date(),
                      leadTime,
                      leadTimeDisplayUnit:
                        option.leadTimeDisplayUnit as LeadTimeUnit,
                    }).toISOString(),
                  )
            }
            onChange={(newMoment) => {
              if (newMoment) {
                dispatch(
                  actions.setLeadTime({
                    quoteItemId: quoteItem.id,
                    leadTime: getLeadTimeFromShipsOnDate({
                      fromDate: orderCreatedDate
                        ? new Date(orderCreatedDate)
                        : new Date(),
                      shipsOnDate: newMoment.toDate(),
                      leadTimeDisplayUnit:
                        option.leadTimeDisplayUnit as LeadTimeUnit,
                    }),
                    quantityId: quantity.id,
                    expediteId: expedite?.id,
                    shipsOn: newMoment.format('YYYY-MM-DD'),
                  }),
                );
              } else {
                dispatch(
                  actions.setLeadTime({
                    quoteItemId: quoteItem.id,
                    leadTime: null,
                    quantityId: quantity.id,
                    expediteId: expedite?.id,
                    shipsOn: null,
                  }),
                );
              }
            }}
          />
        </Td>
        <Td
          textAlign="right"
          fontFamily="data"
          {...cellTextStyles}
        >{`${formatPrice(
          unitPrice,
          quoteItem.rootComponent.unitPricePrecision,
        )}`}</Td>
        <Td
          textAlign="right"
          fontFamily="data"
          {...cellTextStyles}
        >{`${formatPrice(option.totalPrice, 2)}`}</Td>
      </Tr>
      {isSelected && (hasDiscounts || hasAddOns) && (
        <Tr>
          <Td
            colSpan={99}
            backgroundColor="page.plateLight"
            paddingY={0.5}
            paddingLeft={2}
            paddingRight={1}
            innerProps={{
              as: VStack,
              spacing: 0.5,
              alignItems: 'stretch',
            }}
          >
            {hasDiscounts && (
              <DiscountsTable quoteItem={quoteItem} quantity={quantity} />
            )}
            {hasAddOns && (
              <AddOnsTable quoteItem={quoteItem} quantity={quantity} />
            )}
          </Td>
        </Tr>
      )}
    </>
  );
};

const isExpediteSelected = (
  selectedOption: SelectedOption,
  quantity: DigiQuoteQuantity,
  expedite: DigiQuoteExpedite,
) =>
  selectedOption?.quantity?.id === quantity.id &&
  selectedOption?.expedite?.id === expedite.id;

const isQuantitySelected = (
  selectedOption: SelectedOption,
  quantity: DigiQuoteQuantity,
) => selectedOption?.quantity?.id === quantity.id && !selectedOption?.expedite;

type PartSelectorProps = {
  leadTimeDisplayUnit: LeadTimeUnit;
  quoteItem: QuoteItemDigiQuoteData;
};

const PartSelector = ({
  leadTimeDisplayUnit,
  quoteItem,
}: PartSelectorProps) => {
  const dispatch = useDispatch();

  const adjustment = useAppSelector((state) =>
    getAdjustment(state, quoteItem.id),
  );

  const rootComponent = quoteItem.rootComponent;
  const maximumQuoteItemQuantities = useFeatureFlagValue(
    featureFlagKeys.maxQuoteItemQuantities,
  );
  const selectedOption = useAppSelector((state) =>
    getSelectedOption(state, quoteItem.id),
  );

  const searchQuery = useAppSelector(getSearchQuery);

  const adjustmentInputRef = useRef<HTMLInputElement>(null);
  const [shouldFocusAdjustmentInput, setShouldFocusAdjustmentInput] =
    useState(false);

  const addAdditionalCharge = useCallback(
    (additionalCharge: ActionPayload<typeof actions.additionalCharges.add>) => {
      dispatch(actions.additionalCharges.add(additionalCharge));
    },
    [dispatch],
  );

  const addAdjustment = useCallback(
    (adjustment: ActionPayload<typeof actions.adjustments.add>) => {
      dispatch(actions.adjustments.add(adjustment));
      setShouldFocusAdjustmentInput(true);
    },
    [dispatch],
  );

  const selectOptionAction = useCallback(
    (option: ActionPayload<typeof actions.selectOption>) => {
      dispatch(actions.selectOption(option));
    },
    [dispatch],
  );

  const addOnsMap = useMemo(
    () =>
      rootComponent.addOns.reduce(
        (map, addOn) => ({ [addOn.id]: addOn, ...map }),
        {} as Record<number, DigiQuoteAddOn>,
      ),
    [rootComponent],
  );

  const selectOption = useCallback(
    ({ quantity, expedite }: Pick<SelectedOption, 'quantity' | 'expedite'>) => {
      selectOptionAction({
        quoteItemId: quoteItem.id,
        quantity,
        expedite,
        addOns: rootComponent.addOnCells
          .filter(
            (addOnCell) =>
              addOnCell.quantityId === quantity.id &&
              addOnsMap[addOnCell.addOnId]?.isRequired,
          )
          .reduce(
            (addOns, addOnCell) => ({
              ...addOns,
              [addOnCell.addOnId]: addOnCell,
            }),
            {},
          ),
      });
    },
    [addOnsMap, quoteItem.id, rootComponent.addOnCells, selectOptionAction],
  );

  const deselectOption = useCallback(() => {
    selectOptionAction({ quoteItemId: quoteItem.id });
  }, [quoteItem.id, selectOptionAction]);

  const isPartSelected = !!selectedOption || !!adjustment;

  useEffect(() => {
    if (shouldFocusAdjustmentInput && adjustment) {
      adjustmentInputRef.current?.focus();
      setShouldFocusAdjustmentInput(false);
    }
  }, [shouldFocusAdjustmentInput, adjustment, adjustmentInputRef]);

  return (
    <VStack
      width="100%"
      spacing={0}
      alignItems="stretch"
      border="1px solid"
      borderColor="separator.divider"
      borderRadius="md"
      position="relative"
      outline="2px solid"
      outlineColor={isPartSelected ? 'interaction.active' : 'transparent'}
      outlineOffset="-1px"
    >
      <HStack
        spacing={0}
        paddingY={0.5}
        paddingLeft={0.5}
        paddingRight={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <HStack spacing={0}>
          <Text
            size="md"
            lineHeight="md"
            mx={1}
            textAlign="right"
            fontFamily="data"
            width={2}
          >
            {quoteItem.position}
          </Text>
          <Part
            highlight={searchQuery}
            size="lg"
            variant="borderless"
            number={rootComponent.partNumber}
            rev={rootComponent.revision ?? undefined}
            filename={rootComponent.part.filename}
            isItar={quoteItem.exportControlled} // isExportControlled not on part for this API
          >
            <PartThumbnailContainer>
              <PartThumbnail
                partFileUuid={rootComponent.part.primaryFileId}
                filename={rootComponent.part.filename}
                isBoxShadowVisible
                isBorderRadius
                containsVirus={rootComponent.part.containsVirus}
                // isAssembly={rootComponent.part.isAssembly} // isAssembly not on part for this API
              />
            </PartThumbnailContainer>
            <PartLabel />
          </Part>
        </HStack>
        {isPartSelected ? (
          <Tooltip label="Remove from order" placement="bottom-end">
            <IconButton
              size="md"
              aria-label="Remove from order"
              onClick={() => deselectOption()}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </HStack>

      <TableContainer
        border="none"
        borderRadius="none"
        variant="simple"
        size="sm"
      >
        <TableV2
          // border style overrides for the table
          sx={{
            'tr:not(:last-of-type)': {
              td: {
                borderBottomWidth: '0 !important',
              },
            },
            'tbody:not(:last-of-type)': {
              'tr:last-of-type': {
                td: {
                  borderBottomWidth: '1px !important',
                  borderBottomStyle: 'solid !important',
                  borderBottomColor: 'separator.divider !important',
                },
              },
            },
          }}
        >
          <Thead>
            <Tr>
              <Th width={4} />
              <Th width={15} textAlign="right">
                Qty
              </Th>
              <Th width={12} textAlign="right">
                Lead Time
              </Th>
              <Th width={15} textAlign="left">
                Ships On&nbsp;
                <Tooltip label="Calculated based on lead time">
                  <Box as="span">
                    <OutlineCircleInfoIcon
                      boxSize={2}
                      color="feature.secondary"
                    />
                  </Box>
                </Tooltip>
              </Th>
              <Th width={15} textAlign="right">
                Unit Price
              </Th>
              <Th width={15} textAlign="right">
                Total Price
              </Th>
            </Tr>
          </Thead>
          {rootComponent.quantities.map((quantity) => (
            <Tbody key={quantity.id}>
              {quantity.expedites
                .sort((exp1, exp2) => exp1.leadTime - exp2.leadTime)
                .map((expedite, index) => (
                  <OptionDetails
                    darkBg={index === 0}
                    key={expedite.id}
                    quoteItem={quoteItem}
                    quantity={quantity}
                    expedite={expedite}
                    isSelected={isExpediteSelected(
                      selectedOption,
                      quantity,
                      expedite,
                    )}
                    itemSelected={isPartSelected}
                    onSelect={() => selectOption({ quantity, expedite })}
                    onDeselect={() => deselectOption()}
                  />
                ))}
              <OptionDetails
                darkBg={quantity.expedites.length === 0}
                quoteItem={quoteItem}
                quantity={quantity}
                isSelected={isQuantitySelected(selectedOption, quantity)}
                itemSelected={isPartSelected}
                onSelect={() => selectOption({ quantity })}
                onDeselect={() => deselectOption()}
              />
            </Tbody>
          ))}
          {/* adjustment is a row in the table, only one active at a time */}
          {adjustment && (
            <Tbody>
              <Adjustment
                quoteItemId={quoteItem.id}
                leadTimeDisplayUnit={leadTimeDisplayUnit}
                unitPricePrecision={quoteItem.rootComponent.unitPricePrecision}
                inputRef={adjustmentInputRef}
              />
            </Tbody>
          )}
        </TableV2>
        <TableFooter
          flexDirection="column"
          alignItems="stretch"
          minHeight={0}
          padding={0}
        >
          {/* additional charge goes in table footer */}
          <AdditionalCharges quoteItemId={quoteItem.id} />
          <ButtonGroup
            variant="link"
            size="sm"
            justifyContent="flex-end"
            backgroundColor="page.plateLight"
            px={1}
            py={0.5}
          >
            {!adjustment && (
              <Tooltip
                label={`Can't add adjustment. Line item can't have more than ${maximumQuoteItemQuantities} quantity breaks.`}
                isDisabled={
                  rootComponent.quantities.length < maximumQuoteItemQuantities
                }
              >
                <Button
                  onClick={() => addAdjustment({ quoteItemId: quoteItem.id })}
                  isDisabled={
                    rootComponent.quantities.length >=
                    maximumQuoteItemQuantities
                  }
                >
                  Add Adjustment
                </Button>
              </Tooltip>
            )}
            <Button
              onClick={() =>
                addAdditionalCharge({
                  additionalChargeId: uuid(),
                  quoteItemId: quoteItem.id,
                })
              }
            >
              Add Additional Charge
            </Button>
          </ButtonGroup>
        </TableFooter>
      </TableContainer>

      {isPartSelected ? (
        <Circle
          bg="interaction.active"
          position="absolute"
          top={0}
          left={0}
          size={2.5}
          transform="translate(-50%, -50%)"
        >
          <CheckmarkIcon color="white" />
        </Circle>
      ) : null}
    </VStack>
  );
};

export default PartSelector;
